import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { lookupUserByEmail } from '@/lib/api/users';
import { useAuth } from '@/hooks/useAuth';
import type { ProjectMemberRole } from '@/types/backend';

export interface TeamMember {
  email: string;
  role: ProjectMemberRole;
  initials: string;
}

const AVATAR_COLORS: Record<string, { bg: string; text: string }> = {
  JW: { bg: 'bg-purple-200', text: 'text-purple-700' },
  SC: { bg: 'bg-green-200', text: 'text-green-700' },
  MT: { bg: 'bg-orange-200', text: 'text-orange-700' },
};

function hashInitials(initials: string): number {
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

const FALLBACK_COLORS = [
  { bg: 'bg-blue-200', text: 'text-blue-700' },
  { bg: 'bg-rose-200', text: 'text-rose-700' },
  { bg: 'bg-amber-200', text: 'text-amber-700' },
  { bg: 'bg-teal-200', text: 'text-teal-700' },
  { bg: 'bg-indigo-200', text: 'text-indigo-700' },
  { bg: 'bg-pink-200', text: 'text-pink-700' },
  { bg: 'bg-cyan-200', text: 'text-cyan-700' },
  { bg: 'bg-lime-200', text: 'text-lime-700' },
];

function getAvatarColor(initials: string) {
  if (AVATAR_COLORS[initials]) return AVATAR_COLORS[initials];
  return FALLBACK_COLORS[hashInitials(initials) % FALLBACK_COLORS.length];
}

const ROLE_LABELS: Record<ProjectMemberRole, string> = {
  manager: 'Full access',
  editor: 'Edit',
  viewer: 'View only',
};

interface TeamAvatarsProps {
  members: TeamMember[];
  onClick?: () => void;
  onMembersChange?: (members: TeamMember[]) => void;
  maxVisible?: number;
  /** When false, hides add/remove/role-change controls. Defaults to true. */
  canManage?: boolean;
}

export function TeamAvatars({ members, onClick, onMembersChange, maxVisible = 5, canManage = true }: TeamAvatarsProps) {
  const { user } = useAuth();
  const currentUserEmail = user?.email?.toLowerCase() ?? null;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<ProjectMemberRole>('editor');
  const [isAdding, setIsAdding] = useState(false);

  if (members.length === 0) return null;

  const visible = members.slice(0, maxVisible);
  const overflow = members.length - maxVisible;

  const handleClick = () => {
    if (onClick) onClick();
    else setPopoverOpen(!popoverOpen);
  };

  const handleAdd = async () => {
    const email = addEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (members.some(m => m.email.toLowerCase() === email)) {
      toast({ title: 'Already added', variant: 'destructive' });
      return;
    }

    setIsAdding(true);
    try {
      const user = await lookupUserByEmail(email);
      if (!user) {
        toast({
          title: 'User not found',
          description: `${email} is not registered. Ask them to sign up first.`,
          variant: 'destructive',
        });
        return;
      }
      const initials = email.split('@')[0].split('.').map(s => s[0]?.toUpperCase()).join('').slice(0, 2);
      const updated = [...members, { email: user.email, role: addRole, initials }];
      onMembersChange?.(updated);
      setAddEmail('');
      toast({ title: 'Invitation sent', description: `${user.email} added as ${ROLE_LABELS[addRole]}.` });
    } catch (error) {
      console.error('Failed to look up user:', error);
      toast({
        title: 'Lookup failed',
        description: 'Could not validate the email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = (idx: number) => {
    const removed = members[idx];
    onMembersChange?.(members.filter((_, i) => i !== idx));
    toast({ title: 'Member removed', description: `${removed.email} removed.` });
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="relative">
        <button
          onClick={handleClick}
          className="flex items-center -space-x-1.5 hover:opacity-80 transition-opacity cursor-pointer"
          aria-label="Team members"
        >
          {visible.map((member) => {
            const color = getAvatarColor(member.initials);
            return (
              <Tooltip key={member.email}>
                <TooltipTrigger asChild>
                  <div
                    className={`w-7 h-7 rounded-full ${color.bg} ${color.text} flex items-center justify-center text-[10px] font-semibold border-2 border-background`}
                  >
                    {member.initials}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p>{member.email}</p>
                  <p className="text-muted-foreground capitalize">{ROLE_LABELS[member.role]}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          {overflow > 0 && (
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground border-2 border-background">
              +{overflow}
            </div>
          )}
        </button>

        {/* Team management popover */}
        {popoverOpen && !onClick && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setPopoverOpen(false)} />
            <div className="absolute top-full right-0 mt-2 w-[320px] bg-background rounded-lg border border-border shadow-lg z-50">
              <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground">Team</span>
                <button onClick={() => setPopoverOpen(false)} className="p-0.5 rounded hover:bg-muted">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                {members.map((member, i) => {
                  const color = getAvatarColor(member.initials);
                  const isSelf = currentUserEmail !== null && member.email.toLowerCase() === currentUserEmail;
                  const showSelect = canManage && !isSelf;
                  const showRemove = canManage && !isSelf;
                  return (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 group">
                      <div className={`w-6 h-6 rounded-full ${color.bg} ${color.text} flex items-center justify-center text-[9px] font-semibold shrink-0`}>
                        {member.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground truncate">
                          {member.email}
                          {isSelf && <span className="text-muted-foreground ml-1">(you)</span>}
                        </p>
                      </div>
                      {showSelect ? (
                        <select
                          value={member.role}
                          onChange={(e) => {
                            const newRole = e.target.value as ProjectMemberRole;
                            const updated = members.map((m, idx) => idx === i ? { ...m, role: newRole } : m);
                            onMembersChange?.(updated);
                            toast({ title: 'Role updated', description: `${member.email} is now ${ROLE_LABELS[newRole]}.` });
                          }}
                          className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0 border-none outline-none cursor-pointer hover:bg-muted/80"
                        >
                          <option value="manager">Full access</option>
                          <option value="editor">Edit</option>
                          <option value="viewer">View only</option>
                        </select>
                      ) : (
                        <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                          {ROLE_LABELS[member.role]}
                        </span>
                      )}
                      {showRemove && (
                        <button
                          onClick={() => handleRemove(i)}
                          className="p-0.5 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        >
                          <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {canManage && (
                <div className="px-3 py-2 border-t border-border/40 flex gap-1.5">
                  <input
                    value={addEmail}
                    onChange={e => setAddEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                    placeholder="email@firm.com"
                    disabled={isAdding}
                    className="flex-1 text-xs px-2 py-1.5 border border-border rounded bg-background outline-none focus:border-primary/40 min-w-0 disabled:opacity-50"
                  />
                  <select
                    value={addRole}
                    onChange={e => setAddRole(e.target.value as ProjectMemberRole)}
                    disabled={isAdding}
                    className="text-[10px] border border-border rounded px-1 py-1.5 bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="manager">Full access</option>
                    <option value="editor">Edit</option>
                    <option value="viewer">View only</option>
                  </select>
                  <button
                    onClick={handleAdd}
                    disabled={!addEmail.trim() || !addEmail.includes('@') || isAdding}
                    className="p-1.5 rounded bg-foreground text-background hover:bg-foreground/90 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                  >
                    {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
